import { useQuery } from 'react-query'
import { opcaoMenuService, MenuParaPermissao } from '../services/menu'
import { OpcaoMenu, CriarOpcaoMenuDto, AtualizarOpcaoMenuDto } from '../tipos/menu'
import { useMutationWithToast } from '../../../hooks/useMutationWithToast'
import { useToast } from '../../../contexts/NotificationContext'

export function useOpcoesMenu() {
  const toast = useToast()

  return useQuery(['opcoes-menu'], () => opcaoMenuService.obterTodas(), {
    keepPreviousData: true,
    onError: (error: any) => {
      const mensagem = error?.message || 'Erro ao carregar opções de menu'
      toast.error(mensagem)
    },
  })
}

export function useOpcaoMenu(codigo?: number) {
  const toast = useToast()

  return useQuery(
    ['opcao-menu', codigo],
    () => opcaoMenuService.obterPorCodigo(codigo!),
    {
      enabled: !!codigo,
      onError: (error: any) => {
        const mensagem = error?.message || 'Erro ao carregar opção de menu'
        toast.error(mensagem)
      },
    }
  )
}

export function useCreateOpcaoMenu() {
  return useMutationWithToast<OpcaoMenu, CriarOpcaoMenuDto>({
    mutationFn: opcaoMenuService.criar,
    successMessage: 'Opção de menu criada com sucesso!',
    errorMessage: 'Erro ao criar opção de menu',
    invalidateQueries: ['opcoes-menu', 'menu'],
  })
}

export function useUpdateOpcaoMenu() {
  return useMutationWithToast<AtualizarOpcaoMenuDto, { codigo: number; data: AtualizarOpcaoMenuDto }>({
    mutationFn: ({ codigo, data }) => opcaoMenuService.atualizar(codigo, data),
    successMessage: 'Opção de menu atualizada com sucesso!',
    errorMessage: 'Erro ao atualizar opção de menu',
    invalidateQueries: ['opcoes-menu', 'menu', 'opcao-menu', 'opcao-menu-papeis-acesso'],
  })
}

export function useDeleteOpcaoMenu() {
  return useMutationWithToast<void, number>({
    mutationFn: opcaoMenuService.excluir,
    successMessage: 'Opção de menu excluída com sucesso!',
    errorMessage: 'Erro ao excluir opção de menu',
    invalidateQueries: ['opcoes-menu', 'menu'],
  })
}

/** Papéis que têm permissão de leitura para o recurso desta opção (quem vê este item). */
export function usePapeisComAcesso(codigo: number | undefined) {
  return useQuery(
    ['opcao-menu-papeis-acesso', codigo],
    () => opcaoMenuService.obterPapeisComAcesso(codigo!),
    { enabled: !!codigo }
  )
}

/** Lista menus com recurso para tela de permissões por menu. */
export function useMenusParaPermissoes() {
  return useQuery<MenuParaPermissao[]>(
    ['opcao-menu-para-permissoes'],
    () => opcaoMenuService.obterParaPermissoes()
  )
}

/** Atualiza permissões (papéis com acesso) de um item de menu. */
export function useAtualizarPermissoesMenu() {
  return useMutationWithToast<void, { codigo: number; papelCodigos: number[] }>({
    mutationFn: ({ codigo, papelCodigos }) => opcaoMenuService.atualizarPermissoes(codigo, papelCodigos),
    successMessage: 'Permissões atualizadas com sucesso.',
    errorMessage: 'Erro ao atualizar permissões.',
    invalidateQueries: ['opcao-menu-para-permissoes', 'opcao-menu-papeis-acesso', 'menu'],
  })
}
